/**
 * Fallback chain for version resolution.
 * e.g. e2 → e1 → * means: try e2 first, then e1, then wildcard.
 * Items only need entries for versions where they change.
 */
export const VERSION_FALLBACK = {
    e2: 'e1',
    e1: '*',
    d1: '*',
};
/** All concrete (non-wildcard) versions that should appear in flat field output */
export const CONCRETE_VERSIONS = ['d1', 'e1', 'e2'];
/**
 * Resolves a VersionedFieldEntry for a given version by walking the fallback chain.
 * Returns undefined if no entry exists for any version in the chain.
 */
export function resolveVersionEntry(versionMap, version) {
    let v = version;
    while (v) {
        const entry = versionMap[v];
        if (entry) {
            return entry;
        }
        v = VERSION_FALLBACK[v];
    }
    return undefined;
}
/**
 * Converts a VersionedField<T> (the primary source-of-truth format)
 * to the flat Field<T> format for backward compatibility with
 * server-side code (oasisXmlFormat, isActive, etc.).
 */
export function toFlatField(versioned) {
    const result = {};
    for (const [key, versionMap] of Object.entries(versioned)) {
        const versions = [];
        const timepoints = new Set();
        const fieldsMap = {};
        // Resolve each concrete version through the fallback chain
        for (const ver of CONCRETE_VERSIONS) {
            const entry = resolveVersionEntry(versionMap, ver);
            if (entry && entry.timepoint.length > 0) {
                versions.push(ver);
                for (const tp of entry.timepoint) {
                    timepoints.add(tp);
                }
                if (entry.fields) {
                    for (const [subKey, subEntry] of Object.entries(entry.fields)) {
                        if (!fieldsMap[subKey]) {
                            fieldsMap[subKey] = { versions: new Set(), timepoints: new Set() };
                        }
                        fieldsMap[subKey].versions.add(ver);
                        for (const tp of subEntry.timepoint) {
                            fieldsMap[subKey].timepoints.add(tp);
                        }
                        if (subEntry.tags) {
                            fieldsMap[subKey].tags = subEntry.tags;
                        }
                    }
                }
            }
        }
        // Also include wildcard entries directly declared
        if (versionMap['*']) {
            const wildcard = versionMap['*'];
            if (!versions.includes('*')) {
                versions.push('*');
            }
            for (const tp of wildcard.timepoint) {
                timepoints.add(tp);
            }
        }
        if (versions.length === 0) {
            continue;
        }
        const tags = CONCRETE_VERSIONS.map((ver) => resolveVersionEntry(versionMap, ver)?.tags).find((t) => t !== undefined);
        const item = {
            version: versions,
            timepoint: Array.from(timepoints),
            ...(tags ? { tags } : {}),
        };
        if (Object.keys(fieldsMap).length > 0) {
            item.fields = {};
            for (const [subKey, subData] of Object.entries(fieldsMap)) {
                item.fields[subKey] = {
                    version: Array.from(subData.versions),
                    timepoint: Array.from(subData.timepoints),
                    ...(subData.tags ? { tags: subData.tags } : {}),
                };
            }
        }
        result[key] = item;
    }
    return result;
}
export var OASIS;
(function (OASIS) {
    OASIS["SOC"] = "01";
    OASIS["ROC"] = "03";
    OASIS["RCT"] = "04";
    OASIS["OFU"] = "05";
    OASIS["TR"] = "06";
    OASIS["TRD"] = "07";
    OASIS["DTH"] = "08";
    OASIS["DSCH"] = "09";
})(OASIS || (OASIS = {}));
